import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { FulfillmentType } from '../../../enums/fulfillment-type';
import { FulfillmentPlanService } from '../../../services/fulfillment-plan-service';
import Global from '../../../store/global-selectors';
import { emptyArray } from 'ui-components/default-props/empty';

const LABEL_VOID_PROPERTY = 'isLabelVoided';
const EXTERNAL_FULFILLMENT_VOID_PROPERTY = 'isVoided';

const isFulfillmentVoided = (fulfillment) => {
  return (
    fulfillment?.[LABEL_VOID_PROPERTY] ||
    fulfillment?.[EXTERNAL_FULFILLMENT_VOID_PROPERTY] ||
    fulfillment?.externalFulfillment?.[EXTERNAL_FULFILLMENT_VOID_PROPERTY]
  );
};

export const useEntitiesFulfillment = (fulfillmentId) => {
  return useSelector((state) => Global.selectEntitiesFulfillmentById(state, { fulfillmentId }));
};

export const useEntitiesFulfillments = (fulfillmentIds) => {
  return useSelector((state) => Global.selectEntitiesFulfillmentsByIds(state, { fulfillmentIds }));
};

export const useEntitiesFulfillmentProperty = ({ fulfillmentId, property }) => {
  return useSelector((state) => {
    const fulfillment = Global.selectEntitiesFulfillmentById(state, {
      fulfillmentId,
    });
    return fulfillment?.[property];
  });
};

export const useSalesOrderForFulfillmentId = (fulfillmentId) => {
  const fulfillment = useSelector((state) => Global.selectEntitiesFulfillmentById(state, { fulfillmentId })) ?? {};
  const { fulfillmentPlanId } = fulfillment;
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId })) ?? {};
  const [salesOrderId] = fulfillmentPlan.salesOrdersFulfilledIds ?? [];
  return useSelector((state) => Global.selectEntitiesSalesOrderById(state, { salesOrderId })) ?? {};
};

export const useFulfillmentPlanForFulfillmentId = (fulfillmentId) => {
  const fulfillment = useSelector((state) => Global.selectEntitiesFulfillmentById(state, { fulfillmentId })) ?? {};
  const { fulfillmentPlanId } = fulfillment;
  return useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId })) ?? {};
};

export const activeFulfillmentSelector = createSelector(
  (state, { fulfillmentPlanId }) =>
    Global.selectEntitiesFulfillmentsByFulfillmentPlanId(state, {
      fulfillmentPlanId,
    }),
  (fulfillments) => fulfillments.find((fulfillment) => !isFulfillmentVoided(fulfillment)),
);

export const useActiveFulfillmentForFulfillmentPlan = (fulfillmentPlanId) => {
  return useSelector((state) => activeFulfillmentSelector(state, { fulfillmentPlanId }));
};

const activeFulfillmentIdsSelector = createSelector(
  (state, { fulfillmentPlanId }) =>
    Global.selectEntitiesFulfillmentsByFulfillmentPlanId(state, {
      fulfillmentPlanId,
    }),
  (fulfillments) => {
    const activeFulfillments = fulfillments.reduce((acc, fulfillment) => {
      if (!isFulfillmentVoided(fulfillment)) {
        acc.push(fulfillment.fulfillmentId);
      }
      return acc;
    }, []);

    return activeFulfillments.length ? activeFulfillments : emptyArray;
  },
);

// If using this hook for multiple fulfillment plans at the same time, consider creating a new hook that memoizes the selector
export const useActiveFulfillmentIdsForFulfillmentPlan = (fulfillmentPlanId) => {
  return useSelector((state) => activeFulfillmentIdsSelector(state, { fulfillmentPlanId }));
};

const voidableFulfillmentSelector = createSelector(
  (state, { fulfillmentPlanId }) =>
    Global.selectEntitiesFulfillmentsByFulfillmentPlanId(state, {
      fulfillmentPlanId,
    }),
  (fulfillments) => {
    const activeFulfillment = fulfillments.find((fulfillment) => !isFulfillmentVoided(fulfillment));
    return activeFulfillment && !FulfillmentPlanService.isExternal(activeFulfillment) ? activeFulfillment : null;
  },
);

export const useVoidableFulfillmentForFulfillmentPlan = (fulfillmentPlanId) => {
  return useSelector((state) => voidableFulfillmentSelector(state, { fulfillmentPlanId }));
};

export const useTrackingNumberForFulfillmentPlan = (fulfillmentPlanId) => {
  return useSelector((state) => {
    const fulfillment = activeFulfillmentSelector(state, { fulfillmentPlanId }) || {};
    return fulfillment?.trackingInformation?.trackingNumber || undefined;
  });
};

export const useShipToForFulfillment = (fulfillmentId) => {
  return useSelector((state) => {
    const fulfillment = Global.selectEntitiesFulfillmentById(state, { fulfillmentId }) || {};
    const { fulfillmentPlanId, salesOrderId } = fulfillment;
    const { shipTo: fpShipTo } = Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }) || {};
    const { shipTos: soShipTos } = Global.selectEntitiesSalesOrderById(state, { salesOrderId }) || {};
    const [firstSoShipTo] = soShipTos || [];
    return fpShipTo || firstSoShipTo;
  });
};

export const useVoidedFulfillmentsForFulfillmentPlan = (fulfillmentPlanId) => {
  return useSelector((state) => {
    const fulfillments =
      Global.selectEntitiesFulfillmentsByFulfillmentPlanId(state, {
        fulfillmentPlanId,
      }) || [];
    return fulfillments.filter((fulfillment) => isFulfillmentVoided(fulfillment));
  });
};

export const useIsFulfillmentVoided = (fulfillmentId) => {
  return useSelector((state) => {
    const fulfillment = Global.selectEntitiesFulfillmentById(state, { fulfillmentId }) || {};
    if (!fulfillment) {
      return true;
    }
    const { fulfillmentType } = fulfillment;
    return fulfillmentType !== FulfillmentType.Manual && isFulfillmentVoided(fulfillment);
  });
};
